import { apiFetch } from '@/lib/api';
import type { EarlyAccessApplication } from './types';

// Client for the vertical customer lifecycle API (backend slices 1–3). One
// parameterized surface for Sensarra, Sakme, and future verticals — replaces the
// Sensarra-hardcoded early-access calls in the operator console.

export type VerticalLifecycleState =
  | 'lead'
  | 'application_pending'
  | 'application_rejected'
  | 'approved_pending_access'
  | 'checkout_pending'
  | 'active_free'
  | 'active_paid'
  | 'past_due'
  | 'cancelled'
  | 'vertical_deactivated'
  | 'engine_blocked';

export interface VerticalSummary {
  vertical_id: string;
  display_name: string;
  launch_mode: 'early_access' | 'public' | 'closed';
  default_free_tier_key: string;
  paid_tier_keys: string[];
  public_origins: string[];
  customer_home_url: string | null;
  auth_callback_url: string | null;
  skill_shelf_policy: string | null;
}

export interface AdminVerticalsResponse {
  verticals: VerticalSummary[];
  is_director: boolean;
}

export interface VerticalCustomerRow {
  vertical_id: string;
  tenant_id: string | null;
  email: string | null;
  display_name: string | null;
  organization: string | null;
  lifecycle_state: VerticalLifecycleState;
  subscription_status: string | null;
  subscription_tier: string | null;
  effective_tier: string;
  admitted: boolean;
  credit_balance_usd: number;
  application_id: string | null;
  application_status: string | null;
  engine_tenant_status: string | null;
  updated_at: string | null;
}

export interface CustomersResponse {
  vertical_id: string;
  customers: VerticalCustomerRow[];
  count: number;
}

export interface LifecycleEventView {
  event_type: string;
  status: string;
  actor: string | null;
  detail: Record<string, unknown>;
  created_at: string | null;
}

export interface CreditTxnView {
  transaction_type: string;
  amount_usd: number;
  description: string | null;
  created_at: string | null;
}

// Best-effort, read-only correlation of the customer's email across the two
// separate customer tables (engine billing tenants + early-access applications).
// Operator legibility hint only — never a gate, no schema/uniqueness change.
export interface CustomerCrossReference {
  email: string | null;
  application_verticals: string[];
  has_engine_account: boolean;
}

export interface CustomerDetail {
  customer: VerticalCustomerRow;
  credit_summary: {
    total_usd: number;
    expiring_usd: number;
    permanent_usd: number;
    expires_at: string | null;
  };
  recent_transactions: CreditTxnView[];
  recent_events: LifecycleEventView[];
  last_error: LifecycleEventView | null;
  raw: Record<string, unknown>;
  // Optional: absent on older backends / test mocks → the UI shows no hint.
  cross_reference?: CustomerCrossReference;
}

export interface CapabilityItem {
  key: string;
  label: string;
  status: string;
  note: string | null;
}

export interface CapabilityLayer {
  name: string;
  description: string;
  items: CapabilityItem[];
}

export interface CapabilityMap {
  vertical_id: string;
  tenant_id: string;
  lifecycle_state: VerticalLifecycleState;
  admitted: boolean;
  effective_tier: string;
  credit_balance_usd: number;
  substrate: CapabilityLayer;
  vertical_default: CapabilityLayer;
  optional_tenant_skills: CapabilityLayer;
  subscription_gated: CapabilityLayer;
  notes: string[];
}

export interface ApplicationsResponse {
  vertical_id: string;
  applications: EarlyAccessApplication[];
  total: number;
}

// Operator-seat roster (founder handover — CTO-MASTER-OPERATOR-HANDOVER-1).
export interface OperatorEntry {
  email: string;
  source: 'registry' | 'grant';
  granted_by?: string | null;
  granted_at?: string | null;
}

export interface OperatorRoster {
  vertical_id: string;
  static_operators: OperatorEntry[];
  granted_operators: OperatorEntry[];
  effective_operators: string[];
}

function withParams(path: string, params?: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

const v = (vertical: string) => encodeURIComponent(vertical);
const t = (tenantId: string) => encodeURIComponent(tenantId);

export const verticalLifecycleApi = {
  listAdminVerticals: (): Promise<AdminVerticalsResponse> =>
    apiFetch('/api/v1/admin/verticals'),

  listCustomers: (
    vertical: string,
    params?: { status?: string; tier?: string; search?: string; application_status?: string },
  ): Promise<CustomersResponse> =>
    apiFetch(withParams(`/api/v1/admin/verticals/${v(vertical)}/customers`, params)),

  getCustomer: (vertical: string, tenantId: string): Promise<CustomerDetail> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/customers/${t(tenantId)}`),

  getCapabilities: (vertical: string, tenantId: string): Promise<CapabilityMap> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/customers/${t(tenantId)}/capabilities`),

  listApplications: (vertical: string, status?: string): Promise<ApplicationsResponse> =>
    apiFetch(withParams(`/api/v1/admin/verticals/${v(vertical)}/applications`, { status })),

  approve: (
    vertical: string,
    applicationId: string,
    payload: { tenant_id?: string | null; notes?: string | null },
  ): Promise<{ application: EarlyAccessApplication; tenant_id: string; tenant_created: boolean; customer: CustomerDetail }> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/applications/${t(applicationId)}/approve`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  reject: (
    vertical: string,
    applicationId: string,
    payload: { reason: string },
  ): Promise<{ application: EarlyAccessApplication }> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/applications/${t(applicationId)}/reject`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  activateFree: (vertical: string, tenantId: string): Promise<CustomerDetail> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/customers/${t(tenantId)}/activate-free`, { method: 'POST' }),

  deactivate: (vertical: string, tenantId: string, reason?: string): Promise<CustomerDetail> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/customers/${t(tenantId)}/deactivate`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason ?? null }),
    }),

  reactivate: (vertical: string, tenantId: string): Promise<CustomerDetail> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/customers/${t(tenantId)}/reactivate`, { method: 'POST' }),

  resendAccessEmail: (vertical: string, tenantId: string): Promise<{ ok: boolean; result: unknown }> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/customers/${t(tenantId)}/resend-access-email`, { method: 'POST' }),

  // Operator-seat management (founder handover). List is operator-scoped; grant
  // and revoke are super-operator (CTO master / Director) only — the backend
  // re-enforces on every call.
  listOperators: (vertical: string): Promise<OperatorRoster> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/operators`),

  grantOperator: (vertical: string, email: string): Promise<OperatorRoster & { status: string }> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/operators`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  revokeOperator: (vertical: string, email: string): Promise<OperatorRoster & { status: string }> =>
    apiFetch(`/api/v1/admin/verticals/${v(vertical)}/operators/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    }),
};
